import {
  createSlice,
  createEntityAdapter,
  createSelector,
  PayloadAction,
} from "@reduxjs/toolkit";
import { generateEntityStateFromArray, RootState } from "../../store";
import { EntityId } from "@reduxjs/toolkit";
import {
  FeaturedLink,
  Tool,
  ToolCategory,
  ToolCategoryRelation,
  featuredLinks,
  toolCategories,
  tools,
  toolCategoryRelations,
  AuthorInfo,
  authorInfo,
  AuthorData,
} from "./authorProfileAPI";

const featuredLinksAdapter = createEntityAdapter<FeaturedLink>();
// {
//   sortComparer: (item1, item2) =>
//     item1.id === item2.id ? 0 : item1.id > item2.id ? 1 : -1,
// }

const featuredLinksInitialState = featuredLinksAdapter.getInitialState(
  generateEntityStateFromArray(featuredLinks)
);

const toolCategoriesAdapter = createEntityAdapter<ToolCategory>();
const toolCategoriesInitialState = toolCategoriesAdapter.getInitialState(
  generateEntityStateFromArray(toolCategories)
);

const toolsAdapter = createEntityAdapter<Tool>();
const toolsInitialState = toolsAdapter.getInitialState(
  generateEntityStateFromArray(tools)
);

const toolCategoryRelationsAdapter =
  createEntityAdapter<ToolCategoryRelation>();
const toolCategoryRelationsInitialState =
  toolCategoryRelationsAdapter.getInitialState(
    generateEntityStateFromArray(toolCategoryRelations)
  );

// export const fetchAuthorData = createAsyncThunk(
//   "authorProfile/fetchAuthorData",
//   authorProfileAPI.fetchData
// );

interface InitialState {
  featuredLinks: typeof featuredLinksInitialState;
  tools: typeof toolsInitialState & {
    categories: typeof toolCategoriesInitialState;
    relations: typeof toolCategoryRelationsInitialState;
  };
  info: AuthorInfo;
  status: "fulfilled" | "pending" | "rejected";
}

const initialState: InitialState = {
  featuredLinks: featuredLinksInitialState,
  tools: {
    ...toolsInitialState,
    categories: toolCategoriesInitialState,
    relations: toolCategoryRelationsInitialState,
  },
  info: authorInfo,
  status: "fulfilled",
};

const authorSlice = createSlice({
  name: "authorProfile",
  initialState,
  reducers: {
    updateFeaturedLink(state, action: PayloadAction<FeaturedLink>) {
      featuredLinksAdapter.updateOne(state.featuredLinks, {
        id: action.payload.id,
        changes: action.payload,
      });
    },

    deleteFeaturedLink(state, action: PayloadAction<EntityId>) {
      featuredLinksAdapter.removeOne(state.featuredLinks, action.payload);
    },

    setStatus(state, action: PayloadAction<InitialState["status"]>) {
      state.status = action.payload;
    },

    setData(state, action: PayloadAction<AuthorData>) {
      if (action.payload.authorInfo) {
        state.info = action.payload.authorInfo;
      }

      featuredLinksAdapter.removeAll(state.featuredLinks);
      featuredLinksAdapter.addMany(
        state.featuredLinks,
        action.payload.featuredLinks
      );

      toolsAdapter.removeAll(state.tools);
      toolsAdapter.addMany(state.tools, action.payload.tools);

      toolCategoriesAdapter.removeAll(state.tools.categories);
      toolCategoriesAdapter.addMany(
        state.tools.categories,
        action.payload.toolCategories
      );

      toolCategoryRelationsAdapter.removeAll(state.tools.relations);
      toolCategoryRelationsAdapter.addMany(
        state.tools.relations,
        action.payload.toolCategoryRelations
      );
    },

    setAuthorInfo(state, action: PayloadAction<AuthorInfo>) {
      state.info = action.payload;
    },

    setFeaturedLinks(state, action: PayloadAction<FeaturedLink[]>) {
      featuredLinksAdapter.removeAll(state.featuredLinks);
      featuredLinksAdapter.addMany(state.featuredLinks, action.payload);
    },

    setTools(state, action: PayloadAction<Tool[]>) {
      toolsAdapter.removeAll(state.tools);
      toolsAdapter.addMany(state.tools, action.payload);
    },

    setToolCategories(state, action: PayloadAction<ToolCategory[]>) {
      toolCategoriesAdapter.removeAll(state.tools.categories);
      toolCategoriesAdapter.addMany(state.tools.categories, action.payload);
    },

    setToolCategoryRelations(
      state,
      action: PayloadAction<ToolCategoryRelation[]>
    ) {
      toolCategoryRelationsAdapter.removeAll(state.tools.relations);
      toolCategoryRelationsAdapter.addMany(
        state.tools.relations,
        action.payload
      );
    },
  },

  // extraReducers: (builder) => {
  //   // fetch featured links
  //   builder
  //     .addCase(fetchAuthorData.fulfilled, (state, action) => {
  //       if (action.payload.authorInfo) {
  //         state.info = action.payload.authorInfo;
  //       }

  //       featuredLinksAdapter.removeAll(state.featuredLinks);
  //       featuredLinksAdapter.addMany(
  //         state.featuredLinks,
  //         action.payload.featuredLinks
  //       );

  //       toolsAdapter.removeAll(state.tools);
  //       toolsAdapter.addMany(state.tools, action.payload.tools);

  //       toolCategoriesAdapter.removeAll(state.tools.categories);
  //       toolCategoriesAdapter.addMany(
  //         state.tools.categories,
  //         action.payload.toolCategories
  //       );

  //       toolCategoryRelationsAdapter.removeAll(state.tools.relations);
  //       toolCategoryRelationsAdapter.addMany(
  //         state.tools.relations,
  //         action.payload.toolCategoryRelations
  //       );

  //       state.status = "fulfilled";
  //     })
  //     .addCase(fetchAuthorData.pending, (state) => {
  //       state.status = "pending";
  //     })
  //     .addCase(fetchAuthorData.rejected, (state) => {
  //       state.status = "rejected";
  //     });
  // },
});

export const featuredLinksSelectors = featuredLinksAdapter.getSelectors(
  (state: RootState) => state.authorProfile.featuredLinks
);

export const toolsSelectors = toolsAdapter.getSelectors(
  (state: RootState) => state.authorProfile.tools
);

export const toolCategoriesSelectors = toolCategoriesAdapter.getSelectors(
  (state: RootState) => state.authorProfile.tools.categories
);

export const toolCategoryRelationsSelectors =
  toolCategoryRelationsAdapter.getSelectors(
    (state: RootState) => state.authorProfile.tools.relations
  );

export const selectToolsForCategoryID = createSelector(
  [
    toolsSelectors.selectEntities,
    toolCategoryRelationsSelectors.selectAll,
    (state: RootState, categID: EntityId) => categID,
  ],
  (toolsEnt, categRel, categID) => {
    const tools: Tool[] = [];
    for (const tagRel of categRel) {
      if (tagRel?.categoryID === categID) {
        const got = toolsEnt[tagRel.toolID];
        if (got) {
          tools.push(got);
        }
      }
    }
    return tools;
  }
);

export const selectAuthorName = (state: RootState) =>
  state.authorProfile.info.name;
export const selectAuthorTitle = (state: RootState) =>
  state.authorProfile.info.title;
export const selectAuthorDislayPic = (state: RootState) =>
  state.authorProfile.info.displayPic;
export const selectAuthorAboutDesc = (state: RootState) =>
  state.authorProfile.info.aboutDesc;
export const selectAuthorResumeHref = (state: RootState) =>
  state.authorProfile.info.resumeHref;
export const selectAuthorInfo = (state: RootState) => state.authorProfile.info;

export const authorProfileReducer = authorSlice.reducer;
export const authorProfileActions = authorSlice.actions;
